import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { firebaseApp, isFirebaseConfigured } from '../firebase/config';
import { clearAdminSession } from '../auth/adminLogout';
import AdminSidebar from '../components/admin/AdminSidebar';
import { ADMIN_NAV_LINKS } from '../components/admin/adminNavConfig';

const ACCENT = '#C8A96A';

export default function AdminSettings() {
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const [displayUser, setDisplayUser] = useState({ name: 'Admin', email: '' });

  useEffect(() => {
    try {
      const raw = localStorage.getItem('user_data');
      if (raw) {
        const u = JSON.parse(raw);
        setDisplayUser({
          name: u.displayName || u.name || 'Admin',
          email: u.email || '',
        });
      }
    } catch {
      /* ignore */
    }
  }, []);

  const handleLogout = async () => {
    await clearAdminSession();
    navigate('/admin/login');
  };

  const firebaseOn = isFirebaseConfigured() && !!firebaseApp;

  return (
    <div className="min-h-screen flex bg-[#f4f6f8] text-stone-800">
      <AdminSidebar />

      <main className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-stone-200 px-4 md:px-8 py-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-stone-900 tracking-tight">Settings</h1>
            <p className="text-xs text-stone-500 mt-0.5 hidden sm:block">Account and site tools</p>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border border-stone-200 text-stone-700 hover:bg-stone-50"
          >
            <span className="material-symbols-outlined text-lg">language</span>
            Live site
          </Link>
        </header>

        <div className="md:hidden flex gap-2 overflow-x-auto px-4 py-3 border-b border-stone-200 bg-white no-scrollbar">
          {ADMIN_NAV_LINKS.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border ${
                item.isActive(pathname, hash) ? 'border-amber-700/40 bg-amber-50 text-amber-900' : 'border-stone-200 text-stone-600'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="flex-1 p-4 md:p-8 max-w-3xl space-y-6 pb-24">
          <section className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm">
            <h2 className="text-sm font-bold text-stone-800 mb-4">Account</h2>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-stone-500">Name</dt>
                <dd className="font-medium text-stone-900 text-right">{displayUser.name}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-stone-500">Email</dt>
                <dd className="font-medium text-stone-900 text-right break-all">{displayUser.email || '—'}</dd>
              </div>
            </dl>
          </section>

          <section className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm">
            <h2 className="text-sm font-bold text-stone-800 mb-2">Site shortcuts</h2>
            <p className="text-xs text-stone-500 mb-4">Edit content where it appears on the public site.</p>
            <div className="flex flex-wrap gap-2">
              <Link
                to="/services"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-stone-200 hover:bg-stone-50"
              >
                <span className="material-symbols-outlined text-lg">design_services</span>
                Services
              </Link>
              <Link
                to="/dashboard#gallery"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-stone-200 hover:bg-stone-50"
              >
                <span className="material-symbols-outlined text-lg">collections</span>
                Gallery
              </Link>
              <Link
                to="/dashboard#products"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-stone-200 hover:bg-stone-50"
              >
                <span className="material-symbols-outlined text-lg">chair</span>
                Products
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-stone-200 hover:bg-stone-50"
              >
                <span className="material-symbols-outlined text-lg">mail</span>
                Contact
              </Link>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm">
            <h2 className="text-sm font-bold text-stone-800 mb-2">Integrations</h2>
            <p className="text-xs text-stone-500 mb-3">Firebase is used for optional cloud features (e.g. leads).</p>
            <span
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
                firebaseOn ? 'bg-emerald-50 text-emerald-800' : 'bg-stone-100 text-stone-600'
              }`}
            >
              <span className="material-symbols-outlined text-sm">{firebaseOn ? 'cloud_done' : 'cloud_off'}</span>
              {firebaseOn ? 'Firebase connected' : 'Firebase not configured'}
            </span>
          </section>

          <section
            className="rounded-2xl p-5 border border-stone-200"
            style={{ backgroundColor: 'rgba(200, 169, 106, 0.08)' }}
          >
            <h2 className="text-sm font-bold text-stone-800 mb-1">Dashboard</h2>
            <p className="text-xs text-stone-600 mb-3">Return to analytics and overview.</p>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white"
              style={{ backgroundColor: ACCENT }}
            >
              <span className="material-symbols-outlined text-lg">dashboard</span>
              Open dashboard
            </Link>
          </section>
        </div>

        <div className="md:hidden sticky bottom-0 p-4 bg-white border-t border-stone-200 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-stone-800 truncate">{displayUser.name}</p>
            <p className="text-xs text-stone-500 truncate">{displayUser.email}</p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="shrink-0 px-4 py-2 rounded-lg text-sm font-semibold bg-stone-900 text-white"
          >
            Sign out
          </button>
        </div>
      </main>
    </div>
  );
}
